import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import {
  stroot,
  djiIntegration as djiAPI,
  oauthGrants as oauthGrantsAPI,
} from '@birdi/js-sdk';
import { buttonBlue, forms, blocks } from '@birdi/theme';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';

interface DjiStorageConfig {
  bucketType: string;
  bucketName: string;
  bucketAccessKey: string;
  bucketSecretKey: string;
  region: string;
  presetPath: string;
}

interface Integration {
  id: string;
  name: string;
  enabled: boolean;
  provisioned: boolean;
  storageConfig: DjiStorageConfig | null;
  hasWebhookSecret: boolean;
}

const describeError = (err: any, fallback: string): string => {
  if (err?.status) {
    return err?.body?.message
      ? `${err.body.message} (HTTP ${err.status})`
      : `${fallback} (HTTP ${err.status})`;
  }
  return err?.message || fallback;
};

const CopyButton: React.FC<{ value: string; label?: string }> = ({
  value,
  label = 'Copy',
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      css={(theme) => css`
        ${buttonBlue(theme, { baseSize: 'sm' })};
        padding: 0.35em 0.8em;
        white-space: nowrap;
      `}
      onClick={() => {
        navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {});
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  );
};

const Field: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div
    css={css`
      display: flex;
      align-items: center;
      gap: 0.75em;
      margin: 0.4em 0;
    `}
  >
    <span css={css`min-width: 130px; font-weight: 600; font-size: 0.85em;`}>
      {label}
    </span>
    <code
      css={(theme) => css`
        flex: 1;
        padding: 0.4em 0.6em;
        border: 1px solid ${theme.mono20};
        border-radius: 4px;
        font-size: 0.85em;
        overflow-wrap: anywhere;
      `}
    >
      {value || '—'}
    </code>
    {value && <CopyButton value={value} />}
  </div>
);

// A status indicator: a soft-tinted pill with a solid coloured leading dot.
const StatusPill: React.FC<{ dot: string; bg: string; label: string }> = ({
  dot,
  bg,
  label,
}) => (
  <span
    css={css`
      display: inline-flex;
      align-items: center;
      gap: 0.45em;
      padding: 0.3em 0.75em;
      border-radius: 999px;
      background: ${bg};
      font-size: 0.8em;
      font-weight: 600;
      color: #1f2937;
    `}
  >
    <span
      css={css`
        width: 0.55em;
        height: 0.55em;
        border-radius: 50%;
        background: ${dot};
        flex-shrink: 0;
      `}
    />
    {label}
  </span>
);

// Read-only "copy the whole thing" block for config pasted verbatim into a
// FlightHub dialog. One Copy button; also selects-all on focus.
const DetailsBlock: React.FC<{ value: string; rows?: number }> = ({
  value,
  rows = 6,
}) => (
  <>
    <textarea
      readOnly
      rows={rows}
      value={value}
      onFocus={(e) => e.currentTarget.select()}
      css={(theme) => css`
        display: block;
        width: 100%;
        max-width: 640px;
        padding: 0.6em 0.8em;
        border: 1px solid ${theme.mono20};
        border-radius: 4px;
        font-family: monospace;
        font-size: 0.85em;
        line-height: 1.6;
        resize: vertical;
        white-space: pre;
      `}
    />
    <div css={css`margin-top: 0.6em;`}>
      <CopyButton value={value} label="Copy details" />
    </div>
  </>
);

// A grouped setup step: a numbered heading, a one-line "why", and its content.
const Section: React.FC<{
  n: number;
  title: string;
  why?: string;
  children: React.ReactNode;
}> = ({ n, title, why, children }) => (
  <div
    css={(theme) => css`
      border: 1px solid ${theme.mono20};
      border-radius: 8px;
      padding: 1.25em 1.5em;
      margin-bottom: 1em;
      max-width: 700px;
    `}
  >
    <div css={css`display: flex; align-items: center; gap: 0.6em;`}>
      <span
        css={(theme) => css`
          flex-shrink: 0;
          width: 1.6em;
          height: 1.6em;
          border-radius: 50%;
          background: ${theme.midnight};
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.8em;
        `}
      >
        {n}
      </span>
      <span css={css`font-weight: 600; font-size: 1.05em;`}>{title}</span>
    </div>
    {why && (
      <p
        css={(theme) => css`
          color: ${theme.mono60};
          font-size: 0.85em;
          margin: 0.35em 0 1em 2.2em;
        `}
      >
        {why}
      </p>
    )}
    <div css={css`margin-left: 2.2em;`}>{children}</div>
  </div>
);

// Concise numbered instructions for a FlightHub dialog.
const Instructions: React.FC<{ lead: React.ReactNode; children: React.ReactNode }> = ({
  lead,
  children,
}) => (
  <div css={css`margin-top: 0.9em;`}>
    <p css={css`font-size: 0.85em; margin: 0 0 0.3em;`}>{lead}</p>
    <ol
      css={css`
        margin: 0;
        padding-left: 1.3em;
        font-size: 0.85em;
        line-height: 1.7;
      `}
    >
      {children}
    </ol>
  </div>
);

/**
 * Self-serve DJI FlightHub Sync setup for the organisation, grouped by task:
 * (1) storage — the bucket Birdi auto-provisioned + how to point FlightHub at
 * it; (2) event notifications — the Receiving Address + the FlightHub rule
 * that tells Birdi media has landed. Org-admin is enforced server-side; the
 * nav link is likewise gated.
 */
// Shared layout tokens so every integration block aligns on the left and reads
// as one page: a single content width, a consistent section heading, a lead
// paragraph, and a divider between integrations.
const CONTENT_WIDTH = '760px';

const integrationHeading = (theme: any) => css`
  font-size: 1.2em;
  font-weight: 700;
  color: ${theme.midnight};
  margin: 0 0 0.4em;
`;

const integrationLead = (theme: any) => css`
  max-width: ${CONTENT_WIDTH};
  margin: 0 0 1.25em;
  line-height: 1.55;
  color: ${theme.mono70};
`;

const integrationSubheading = (theme: any) => css`
  font-size: 0.95em;
  font-weight: 600;
  color: ${theme.midnight};
  margin: 1.5em 0 0.6em;
`;

const integrationDivider = (theme: any) => css`
  border: 0;
  border-top: 1px solid ${theme.mono20};
  max-width: ${CONTENT_WIDTH};
  margin: 2.75em 0 2em;
`;

interface ConnectedAppGrant {
  id: string;
  clientName: string;
  scope: string;
  createdAt: string;
  lastUsedAt: string | null;
}

const scopeLabel = (scope: string): string =>
  scope.includes('write') ? 'Read and act' : 'Read-only';

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

/**
 * Connected apps: the AI assistants the signed-in user has linked to Birdi
 * through the MCP connector, each revocable here. Grants are per USER (not the
 * org), so this lists the current user's own connections; revoking kills the
 * connection's refresh token at once and its access tokens on their next call.
 */
// Both connection lists reload when either one disconnects something, so an
// admin revoking their own app in the org table also clears it from theirs.
interface ConnectionListProps {
  refreshKey: number;
  onChange: () => void;
}

const ConnectedApps: React.FC<ConnectionListProps> = ({
  refreshKey,
  onChange,
}) => {
  const [grants, setGrants] = useState<ConnectedAppGrant[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = (): void => {
    oauthGrantsAPI
      .list(stroot('cnctd0'))
      .then((res: any) => {
        setGrants(res.body ?? []);
        setLoadError(null);
      })
      .catch((err: any) =>
        setLoadError(describeError(err, 'Could not load your connected apps.')),
      );
  };

  useEffect(load, [refreshKey]);

  const disconnect = (grant: ConnectedAppGrant): void => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Disconnect ${grant.clientName}? It will immediately lose access to your Birdi account.`,
      )
    )
      return;
    setActionError(null);
    setRevokingId(grant.id);
    oauthGrantsAPI
      .revoke(stroot('cnctd1'), grant.id)
      .then(() => {
        setGrants((prev) =>
          (prev ?? []).filter((candidate) => candidate.id !== grant.id),
        );
        onChange();
      })
      .catch((err: any) =>
        setActionError(describeError(err, 'Could not disconnect that app.')),
      )
      .finally(() => setRevokingId(null));
  };

  return (
    <div
      css={(theme) => css`
        border: 1px solid ${theme.mono20};
        border-radius: 8px;
        padding: 0.25em 1.5em 0.75em;
        max-width: ${CONTENT_WIDTH};
      `}
    >
      {loadError && (
        <p css={css`color: #dc2626; font-size: 0.85em;`}>{loadError}</p>
      )}
      {grants === null && !loadError && (
        <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.85em;`}>
          Loading…
        </p>
      )}
      {grants && grants.length === 0 && (
        <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.85em;`}>
          No AI assistants are connected.
        </p>
      )}

      {grants && grants.length > 0 && (
        <div>
          {grants.map((grant) => (
            <div
              key={grant.id}
              css={(theme) => css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1em;
                padding: 0.9em 0;
                border-top: 1px solid ${theme.mono20};
                &:first-of-type {
                  border-top: 0;
                }
              `}
            >
              <div>
                <div css={css`font-weight: 600;`}>{grant.clientName}</div>
                <div
                  css={(theme) => css`
                    color: ${theme.mono60};
                    font-size: 0.8em;
                    margin-top: 0.2em;
                  `}
                >
                  {scopeLabel(grant.scope)} · Connected{' '}
                  {formatDate(grant.createdAt)} · Last used{' '}
                  {formatDate(grant.lastUsedAt)}
                </div>
              </div>
              <button
                type="button"
                css={css`
                  flex-shrink: 0;
                  background: none;
                  border: 1px solid #dc2626;
                  color: #dc2626;
                  border-radius: 4px;
                  padding: 0.5em 1em;
                  font-size: 0.85em;
                  font-weight: 600;
                  cursor: pointer;
                  &:disabled {
                    opacity: 0.5;
                    cursor: default;
                  }
                `}
                onClick={() => disconnect(grant)}
                disabled={revokingId === grant.id}
              >
                {revokingId === grant.id ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          ))}
        </div>
      )}

      {actionError && (
        <p css={css`color: #dc2626; font-size: 0.85em; margin-top: 0.8em;`}>
          {actionError}
        </p>
      )}
    </div>
  );
};

/**
 * How to add Birdi to an AI assistant: the connector URL (this environment's
 * API, so UAT and previews hand out their own) plus the Claude Code command.
 * Hidden behind the plan feature the MCP endpoint itself enforces, so we never
 * hand out a URL that would answer 403.
 */
const ConnectorSetup: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  if (!enabled) {
    return (
      <p
        css={(theme) => css`
          color: ${theme.mono60};
          font-size: 0.85em;
          max-width: ${CONTENT_WIDTH};
        `}
      >
        Connecting AI assistants isn&apos;t included in your organisation&apos;s
        plan.
      </p>
    );
  }
  const connectorUrl = `${process.env.API_URL ?? ''}/v1/mcp`;
  return (
    <div
      css={(theme) => css`
        border: 1px solid ${theme.mono20};
        border-radius: 8px;
        padding: 0.9em 1.5em 1em;
        max-width: ${CONTENT_WIDTH};
      `}
    >
      <Field label="Connector URL" value={connectorUrl} />
      <Field
        label="Claude Code"
        value={`claude mcp add --transport http birdi ${connectorUrl}`}
      />
      <ol
        css={(theme) => css`
          margin: 0.9em 0 0;
          padding-left: 1.2em;
          font-size: 0.85em;
          line-height: 1.7;
          color: ${theme.mono70};
        `}
      >
        <li>
          In Claude, add a custom connector (<b>Customize › Connectors</b>) and
          paste the connector URL. Any assistant that supports remote MCP
          connectors works the same way.
        </li>
        <li>Sign in to Birdi when it asks.</li>
        <li>
          Choose <b>Read-only</b> or <b>Read and act</b>. The connection then
          appears under Your connections below.
        </li>
      </ol>
    </div>
  );
};

interface OrganisationConnection {
  id: string;
  clientName: string;
  scope: string;
  createdAt: string;
  lastUsedAt: string | null;
  user: { id: string; name: string | null; email: string | null };
  // Null when the member's connection currently points at an organisation
  // outside this one (the API never names it).
  workspace: { id: string; name: string | null } | null;
  outsideOrganisation: boolean;
}

/**
 * Org-admin view: who in the organisation has which AI apps connected, each
 * one disconnectable here (e.g. offboarding without removing the person).
 * Removing someone from the organisation also deletes their connections.
 */
const OrganisationConnections: React.FC<ConnectionListProps> = ({
  refreshKey,
  onChange,
}) => {
  const [rows, setRows] = useState<OrganisationConnection[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    oauthGrantsAPI
      .listOrganisation(stroot('cnctd2'))
      .then((res: any) => {
        setRows(res.body ?? []);
        setLoadError(null);
      })
      .catch((err: any) =>
        setLoadError(
          describeError(err, "Could not load your organisation's connections."),
        ),
      );
  }, [refreshKey]);

  const disconnect = (row: OrganisationConnection): void => {
    const who = row.user.name || row.user.email || 'this person';
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Disconnect ${row.clientName} for ${who}? It will immediately lose access to Birdi.`,
      )
    )
      return;
    setActionError(null);
    setRevokingId(row.id);
    oauthGrantsAPI
      .revokeInOrganisation(stroot('cnctd3'), row.id)
      .then(() => {
        setRows((prev) =>
          (prev ?? []).filter((candidate) => candidate.id !== row.id),
        );
        onChange();
      })
      .catch((err: any) =>
        setActionError(describeError(err, 'Could not disconnect that app.')),
      )
      .finally(() => setRevokingId(null));
  };

  const muted = (theme: any) => css`
    color: ${theme.mono60};
    font-size: 0.85em;
  `;

  if (loadError) {
    return <p css={css`color: #dc2626; font-size: 0.85em;`}>{loadError}</p>;
  }
  if (rows === null) return <p css={muted}>Loading…</p>;
  if (rows.length === 0) {
    return (
      <p css={muted}>
        No one in your organisation has connected an AI assistant yet.
      </p>
    );
  }

  const people = new Set(rows.map((row) => row.user.id)).size;
  // Only worth a column when connections span more than one workspace, or
  // when one points outside the organisation.
  const showWorkspace =
    rows.some((row) => row.outsideOrganisation) ||
    new Set(rows.map((row) => row.workspace?.id)).size > 1;
  // Every row is its own grid, so each column needs the same width in every
  // row: Access and Last used get fixed widths that fit their longest value
  // ("Read and act", a date); person, app and workspace share the rest and
  // truncate.
  const columns = showWorkspace
    ? 'minmax(0, 1.6fr) minmax(0, 1fr) 7.5em minmax(0, 1fr) 6.5em 6.5em'
    : 'minmax(0, 2fr) minmax(0, 1.2fr) 7.5em 6.5em 6.5em';
  // One line per cell; long values truncate and show in full on hover.
  const cell = css`
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
  const rowCss = (theme: any) => css`
    display: grid;
    grid-template-columns: ${columns};
    gap: 0.9em;
    align-items: center;
    padding: 0.8em 0;
    border-top: 1px solid ${theme.mono20};
    font-size: 0.9em;
  `;

  return (
    <div>
      <p css={(theme) => css`${muted(theme)}; margin: 0 0 0.6em;`}>
        {rows.length} {rows.length === 1 ? 'connection' : 'connections'} ·{' '}
        {people} {people === 1 ? 'person' : 'people'}
      </p>
      <div
        css={(theme) => css`
          border: 1px solid ${theme.mono20};
          border-radius: 8px;
          padding: 0 1.5em;
          max-width: ${CONTENT_WIDTH};
          overflow-x: auto;
        `}
      >
        <div
          css={(theme) => css`
            ${rowCss(theme)};
            border-top: 0;
            font-weight: 600;
            color: ${theme.midnight};
            /* Shrink the labels, not the row: the column widths are in em, so
               the row must share the data rows' font size to line up. */
            & > span {
              font-size: 0.95em;
            }
          `}
        >
          <span>Person</span>
          <span>App</span>
          <span>Access</span>
          {showWorkspace && <span>Workspace</span>}
          <span>Last used</span>
          {/* Keeps the header grid in step with the Disconnect column. */}
          <span aria-hidden="true" css={css`width: 6.5em;`} />
        </div>
        {rows.map((row, i) => {
          // Rows arrive sorted by person, so a person's apps sit together:
          // name them once and only rule a line between people.
          const firstOfPerson = i === 0 || rows[i - 1].user.id !== row.user.id;
          return (
          <div
            key={row.id}
            css={(theme) => css`
              ${rowCss(theme)};
              ${firstOfPerson ? '' : 'border-top: 0; padding-top: 0;'}
            `}
          >
            <div css={css`min-width: 0;`}>
              {firstOfPerson && (
                <>
                  <div
                    css={css`
                      font-weight: 600;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                    title={row.user.name || row.user.email || undefined}
                  >
                    {row.user.name || row.user.email || 'Unknown user'}
                  </div>
                  {row.user.name && row.user.email && (
                    <div
                      css={(theme) => css`
                        ${muted(theme)};
                        font-size: 0.8em;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      `}
                      title={row.user.email}
                    >
                      {row.user.email}
                    </div>
                  )}
                </>
              )}
            </div>
            <span css={cell} title={row.clientName}>
              {row.clientName}
            </span>
            <span css={cell}>{scopeLabel(row.scope)}</span>
            {showWorkspace && (
              <span
                css={(theme) => css`
                  ${cell};
                  ${row.outsideOrganisation ? muted(theme) : ''};
                `}
                title={
                  row.outsideOrganisation
                    ? 'Currently pointed at another of this person’s organisations. It can be switched back here, so you can still disconnect it.'
                    : row.workspace?.name || undefined
                }
              >
                {row.outsideOrganisation
                  ? 'Another organisation'
                  : row.workspace?.name || '—'}
              </span>
            )}
            <span css={cell}>{formatDate(row.lastUsedAt)}</span>
            <button
              type="button"
              css={css`
                width: 6.5em;
                background: none;
                border: 1px solid #dc2626;
                color: #dc2626;
                border-radius: 4px;
                padding: 0.35em 0;
                font-size: 0.85em;
                font-weight: 600;
                cursor: pointer;
                &:disabled {
                  opacity: 0.5;
                  cursor: default;
                }
              `}
              onClick={() => disconnect(row)}
              disabled={revokingId === row.id}
            >
              {revokingId === row.id ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
          );
        })}
      </div>
      {actionError && (
        <p css={css`color: #dc2626; font-size: 0.85em; margin-top: 0.8em;`}>
          {actionError}
        </p>
      )}
    </div>
  );
};

export const Integrations = (): React.ReactElement => {
  const meta = useMetaSelector();
  const canManage = meta.permissions?.includes('organisation:admin');
  const [connectionsVersion, setConnectionsVersion] = useState(0);
  const refreshConnections = (): void =>
    setConnectionsVersion((version) => version + 1);

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  // Only available right after create/rotate — shown once.
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);

  const [orgToken, setOrgToken] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    djiAPI
      .get(stroot('djisg0'))
      .then((res: any) => setIntegration(res.body?.integration ?? null))
      .catch(() => setLoadError('Failed to load the DJI integration.'))
      .finally(() => setLoaded(true));
  }, []);

  const applyResponse = (res: any, fallback: string): boolean => {
    if (!res.body?.id) {
      setError(`${fallback} (HTTP ${res.status ?? '?'})`);
      return false;
    }
    setIntegration(res.body);
    if (res.body.token) setWebhookUrl(res.body.webhookUrl);
    setProvisionError(res.body.provisionError ?? null);
    return true;
  };

  const handleCreate = () => {
    setBusy(true);
    setError(null);
    djiAPI
      .create(stroot('djisc0'))
      .then((res: any) => {
        if (applyResponse(res, 'Setup did not return the expected data')) {
          // Analytics: an org has switched the DJI FlightHub integration on.
          posthog?.capture('dji integration activated', {
            integrationId: res.body.id,
            organisationId: meta.organisation?.id,
            provisioned: Boolean(res.body.provisioned),
          });
        }
      })
      .catch((err: any) => setError(describeError(err, 'Failed to set up the integration.')))
      .finally(() => setBusy(false));
  };

  const handleProvision = () => {
    setBusy(true);
    setError(null);
    djiAPI
      .provision(stroot('djisp0'))
      .then((res: any) => applyResponse(res, 'Provisioning did not return the expected data'))
      .catch((err: any) => setError(describeError(err, 'Provisioning failed.')))
      .finally(() => setBusy(false));
  };

  const handleRotate = () => {
    setBusy(true);
    setError(null);
    djiAPI
      .rotateToken(stroot('djisr0'))
      .then((res: any) => applyResponse(res, 'Token rotation failed'))
      .catch((err: any) => setError(describeError(err, 'Failed to rotate token.')))
      .finally(() => setBusy(false));
  };

  const handleToggleEnabled = () => {
    if (!integration) return;
    setBusy(true);
    setError(null);
    djiAPI
      .update(stroot('djise0'), { enabled: !integration.enabled })
      .then((res: any) => {
        if (res.body) setIntegration(res.body);
      })
      .catch((err: any) => setError(describeError(err, 'Failed to update.')))
      .finally(() => setBusy(false));
  };

  const handleSaveOrgToken = () => {
    setBusy(true);
    setError(null);
    djiAPI
      .update(stroot('djist0'), { webhookSecret: orgToken })
      .then((res: any) => {
        if (res.body) setIntegration(res.body);
        setOrgToken('');
      })
      .catch((err: any) => setError(describeError(err, 'Failed to save token.')))
      .finally(() => setBusy(false));
  };

  const refetch = () =>
    djiAPI
      .get(stroot('djiref'))
      .then((res: any) => setIntegration(res.body?.integration ?? null))
      .catch(() => {});

  // Provisioning/rotation run async in a worker — poll until storage is ready.
  useEffect(() => {
    if (!integration || integration.provisioned) return undefined;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      if (tries > 30) {
        clearInterval(id);
        return;
      }
      refetch();
    }, 4000);
    return () => clearInterval(id);
  }, [integration?.id, integration?.provisioned]);

  const handleRotateCredentials = () => {
    setBusy(true);
    setError(null);
    djiAPI
      .rotateCredentials(stroot('djircr'))
      .then(() => {
        // The worker rewrites the SSM secret; re-fetch to surface the new AK/SK.
        setTimeout(refetch, 2500);
        setTimeout(refetch, 6000);
      })
      .catch((err: any) => setError(describeError(err, 'Failed to rotate credentials.')))
      .finally(() => setBusy(false));
  };

  const handleDisconnect = () => {
    if (
      !window.confirm(
        'Disconnect DJI FlightHub? This removes the integration and its storage credentials. Existing maps are kept, but FlightHub will stop syncing new media.',
      )
    )
      return;
    setBusy(true);
    setError(null);
    djiAPI
      .deprovision(stroot('djidis'))
      .then(() => {
        setIntegration(null);
        setWebhookUrl(null);
      })
      .catch((err: any) => setError(describeError(err, 'Failed to disconnect.')))
      .finally(() => setBusy(false));
  };

  const helpText = (theme: any) => css`
    color: ${theme.mono60};
    font-size: 0.85em;
    max-width: 620px;
    margin: 0 0 0.75em;
  `;
  const linkButton = (theme: any) => css`
    background: none;
    border: none;
    padding: 0;
    color: ${theme.ocean};
    font-size: 0.85em;
    cursor: pointer;
    text-decoration: underline;
  `;

  const sc = integration?.storageConfig;
  const storageDetails = sc
    ? [
        ['Bucket Type', sc.bucketType],
        ['Bucket Name', sc.bucketName],
        ['Bucket AK', sc.bucketAccessKey],
        ['Bucket SK', sc.bucketSecretKey],
        ['Region', sc.region],
        ['Preset Path', sc.presetPath],
      ]
        .map(([label, value]) => `${label}: ${value ?? ''}`)
        .join('\n')
    : '';

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>Integrations</h2>
      </div>
      <div css={blocks.pageGraySection}>
        <p>Connect Birdi to the tools and AI assistants your team already uses.</p>
      </div>

      <div css={css`padding: 1.5em 1em 2.5em;`}>
        <h3 css={integrationHeading}>DJI FlightHub 2</h3>
        <p css={integrationLead}>
          Connect FlightHub so the drone media your team captures uploads
          straight into Birdi as new maps — no exporting from FlightHub and
          re-uploading by hand. Set this up once, and every synced flight then
          appears and processes automatically.
        </p>

        <div
          css={(theme) => css`
            border: 1px solid ${theme.mono20};
            border-radius: 8px;
            padding: 1em 1.25em;
            margin-bottom: 1.75em;
            max-width: ${CONTENT_WIDTH};
            background: ${theme.sand};
          `}
        >
          <div css={css`font-weight: 600; margin-bottom: 0.5em;`}>
            Before you start
          </div>
          <ul
            css={css`
              margin: 0;
              padding-left: 1.2em;
              font-size: 0.85em;
              line-height: 1.7;
            `}
          >
            <li>
              Requires <b>DJI FlightHub 2 on a Business plan or higher</b> —
              FlightHub Sync isn't available on lower tiers.
            </li>
          </ul>
          <details css={css`margin-top: 0.6em; font-size: 0.85em;`}>
            <summary css={css`cursor: pointer; font-weight: 600;`}>
              Supported devices
            </summary>
            <ul
              css={css`
                margin: 0.5em 0 0;
                padding-left: 1.2em;
                line-height: 1.7;
              `}
            >
              <li>
                <b>Docks:</b> DJI Dock 3, DJI Dock 2, DJI Dock.
              </li>
              <li>
                <b>Drones:</b> Matrice 400, Matrice 350 RTK, Matrice 30 Series,
                Matrice 4 Series, Matrice 4D Series, Mavic 3 Enterprise Series
                (excluding Mavic 3 Multispectral), and others.
              </li>
              <li>
                <b>Payloads:</b> Zenmuse H30 Series, Zenmuse H20 Series, Zenmuse
                S1, Zenmuse V1.
              </li>
            </ul>
          </details>
        </div>

        {!canManage && (
          <p css={css`color: #b45309; max-width: 620px;`}>
            You need organisation admin access to manage integrations.
          </p>
        )}

        {loadError && <p css={css`color: red;`}>{loadError}</p>}
        {error && <p css={css`color: red;`}>{error}</p>}

        {canManage && loaded && !integration && (
          <div css={css`max-width: 620px;`}>
            <p css={(theme) => helpText(theme)}>
              We'll create your organisation's own private, secured intake
              storage automatically. Then you'll paste a couple of values into
              FlightHub — it takes a few minutes.
            </p>
            <button
              type="button"
              css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 1em 1.5em;`}
              onClick={handleCreate}
              disabled={busy}
            >
              {busy ? 'Setting up…' : 'Connect DJI FlightHub'}
            </button>
          </div>
        )}

        {canManage && integration && (
          <div>
            <div css={css`display: flex; gap: 0.6em; align-items: center; margin-bottom: 1.25em;`}>
              {integration.provisioned ? (
                <StatusPill dot="#16a34a" bg="#dcfce7" label="Connected" />
              ) : (
                <StatusPill dot="#dc2626" bg="#fee2e2" label="Not connected" />
              )}
              {!integration.enabled ? (
                <StatusPill dot="#9ca3af" bg="#f3f4f6" label="Paused" />
              ) : integration.provisioned ? (
                <StatusPill dot="#9ca3af" bg="#f3f4f6" label="Receiving media" />
              ) : (
                <StatusPill dot="#f59e0b" bg="#fef3c7" label="Receiving media" />
              )}
            </div>

            <Section
              n={1}
              title="Configure storage"
              why="Where FlightHub sends your media. Birdi has created a private bucket for your organisation — point FlightHub at it with these values."
            >
              {integration.storageConfig ? (
                <>
                  <DetailsBlock value={storageDetails} rows={6} />
                  <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.8em; margin: 0.5em 0 0;`}>
                    The Bucket AK/SK are credentials for your own storage — treat
                    them like a password.
                  </p>
                  <Instructions lead={<>In DJI FlightHub:</>}>
                    <li>
                      Top-right <b>profile</b> → <b>My Organisation</b> → the
                      settings (cog) → <b>+ Add Third-Party Cloud Service</b>{' '}
                      under <b>FlightHub Sync</b>.
                    </li>
                    <li>
                      Under <b>Livestream &amp; Data Management</b>, open{' '}
                      <b>File Synchronization</b>.
                    </li>
                    <li>
                      In the <b>Bucket storage configuration</b>, set Bucket Type{' '}
                      <b>AWS S3</b> and paste the values above → run{' '}
                      <b>Connectivity Check</b> to confirm it connects → Save.
                    </li>
                    <li>
                      In <b>Project Sync Rules</b>, add a rule for each project
                      you want in Birdi and select{' '}
                      <b>Media Files: FlightHub → Third-Party</b>. FlightHub has
                      no "all projects" option, so add a rule for each new
                      project as you fly it.
                    </li>
                  </Instructions>
                </>
              ) : (
                <>
                  <p css={css`color: #b45309; font-size: 0.85em; margin: 0 0 0.6em;`}>
                    Setting up your storage…
                    {provisionError ? ` failed: ${provisionError}` : ''} This is
                    automatic and usually takes a moment.
                  </p>
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleProvision}
                    disabled={busy}
                  >
                    {busy ? 'Retrying…' : 'Retry'}
                  </button>
                </>
              )}
            </Section>

            <Section
              n={2}
              title="Set up event notifications"
              why="How FlightHub tells Birdi that new media has landed, so Birdi can build the map."
            >
              {webhookUrl ? (
                <Field label="Receiving Address" value={webhookUrl} />
              ) : (
                <>
                  <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.85em; margin: 0 0 0.6em;`}>
                    Your Receiving Address is shown only once, when issued.
                    Generate it now — this replaces any address issued before, so
                    if FlightHub is already connected, re-paste the new one below.
                  </p>
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleRotate}
                    disabled={busy}
                  >
                    {busy ? 'Generating…' : 'Generate Receiving Address'}
                  </button>
                </>
              )}
              <Instructions lead={<>In the same FlightHub Sync settings:</>}>
                <li>
                  Under <b>System and Information</b>, open{' '}
                  <b>EventAPI Event Notification</b>.
                </li>
                <li>
                  Click <b>Create</b> next to <b>Notification Rule
                  Configuration</b>.
                </li>
                <li>
                  Paste the <b>Receiving Address</b> above, select the projects
                  you're syncing, tick <b>only</b> "File Sync Notification" →{' '}
                  <b>Confirm</b>.
                </li>
              </Instructions>
            </Section>

            <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.85em; max-width: 700px; margin: 0.5em 0 1.5em;`}>
              That's it. Fly a mission or let existing media sync, and a new map
              appears in Birdi automatically.
            </p>

            <button
              type="button"
              css={(theme) => linkButton(theme)}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide advanced settings' : 'Advanced settings'}
            </button>

            {showAdvanced && (
              <div css={(theme) => css`margin-top: 1em; padding-top: 1em; border-top: 1px solid ${theme.mono20}; max-width: 640px;`}>
                <div css={css`font-weight: 600; font-size: 0.9em; margin-bottom: 0.3em;`}>
                  Signature verification (optional)
                </div>
                <p css={(theme) => helpText(theme)}>
                  Paste your FlightHub organization token to have Birdi verify the
                  signature on incoming events.{' '}
                  {integration.hasWebhookSecret
                    ? 'A token is currently stored.'
                    : 'No token stored yet.'}
                </p>
                <div css={css`display: flex; gap: 0.75em; margin-bottom: 1.25em;`}>
                  <input
                    css={(theme) => css`${forms.textInput(theme)}; flex: 1;`}
                    value={orgToken}
                    placeholder="FlightHub organization token"
                    onChange={(e) => setOrgToken(e.target.value)}
                  />
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleSaveOrgToken}
                    disabled={busy || !orgToken}
                  >
                    Save
                  </button>
                </div>

                <div css={css`font-weight: 600; font-size: 0.9em; margin-bottom: 0.5em;`}>
                  Manage
                </div>
                <div css={css`display: flex; gap: 0.75em; flex-wrap: wrap;`}>
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleRotate}
                    disabled={busy}
                  >
                    Regenerate Receiving Address
                  </button>
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleRotateCredentials}
                    disabled={busy || !integration.provisioned}
                  >
                    Rotate storage credentials
                  </button>
                  <button
                    type="button"
                    css={(theme) => css`${buttonBlue(theme, { baseSize: 'sm' })}; padding: 0.5em 1em;`}
                    onClick={handleToggleEnabled}
                    disabled={busy}
                  >
                    {integration.enabled ? 'Pause' : 'Resume'}
                  </button>
                </div>

                <p css={(theme) => css`color: ${theme.mono60}; font-size: 0.8em; margin: 1em 0 0.4em;`}>
                  Rotating replaces the storage AK/SK — the new values appear in
                  Step 1 above; re-paste them into FlightHub. Disconnecting
                  removes the integration and its storage (existing maps are
                  kept).
                </p>
                <button
                  type="button"
                  css={css`
                    background: none;
                    border: 1px solid #dc2626;
                    color: #dc2626;
                    border-radius: 4px;
                    padding: 0.5em 1em;
                    font-size: 0.85em;
                    font-weight: 600;
                    cursor: pointer;
                  `}
                  onClick={handleDisconnect}
                  disabled={busy}
                >
                  Disconnect DJI FlightHub
                </button>
              </div>
            )}
          </div>
        )}

        <hr css={integrationDivider} />

        <h3 css={integrationHeading}>Connected apps</h3>
        <p css={integrationLead}>
          AI assistants connected to Birdi through the connector (for example
          Claude). Each one acts as the person who connected it, with the access
          level they granted.
        </p>

        <h4 css={integrationSubheading}>Add Birdi to your AI assistant</h4>
        <ConnectorSetup
          enabled={Boolean(meta.features?.includes('mcp-connection'))}
        />

        <h4 css={integrationSubheading}>Your connections</h4>
        <ConnectedApps
          refreshKey={connectionsVersion}
          onChange={refreshConnections}
        />

        {canManage && (
          <>
            <h4 css={integrationSubheading}>Across your organisation</h4>
            <p
              css={(theme) => css`
                ${integrationLead(theme)};
                font-size: 0.9em;
                margin-bottom: 0.9em;
              `}
            >
              Everyone in your organisation who has connected an AI assistant.
              Disconnect any of them to revoke its access straight away.
              Removing someone from the organisation also disconnects theirs.
            </p>
            <OrganisationConnections
              refreshKey={connectionsVersion}
              onChange={refreshConnections}
            />
          </>
        )}
      </div>
    </div>
  );
};
